/*
 * ET - About Timeline (sections/et-about-timeline.liquid)
 * <et-about-timeline-slider>: the milestones are a static row from 1440px (where the five Figma slides fill the 1279px
 * inner width of the track exactly) and a Swiper slider below 1440px (slide width from CSS, 24px gap, Scrollbar module
 * on the shared .et-slider-progress bar when it is shown, optional autoplay from data-autoplay / data-autoplay-delay,
 * off for prefers-reduced-motion and paused while a milestone is selected in the editor). Left/right arrow keys move
 * the slider while it is in view (Page Up/Down keep scrolling the page); slide labels use the theme's translated
 * "Item {index} of {count}" string from data-slide-label.
 * Swiper is created/destroyed on the breakpoint change and destroyed on disconnect, so theme editor re-renders
 * (shopify:section:load / unload) never leave a duplicate instance. Blocks selected in the editor slide into view.
 * Without JS (or if Swiper cannot load) the track stays a native scroll-snap row - see assets/et-about-timeline.css.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-about-timeline-slider')) return;

  const SLIDER_QUERY = '(max-width: 1439px)';
  const SLIDE_GAP = 24;
  /* Figma draws the mobile thumb 30px wide in the 96px inner track (the natural ratio lands on ~28px) */
  const PROGRESS_THUMB_SIZE = 30;
  const DEFAULT_AUTOPLAY_DELAY = 5000;
  const SLIDE_SELECTOR = '[data-et-about-timeline-slide]';

  class EtAboutTimelineSlider extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.loading = false;
      this.pendingIndex = null;
      this.blockSelected = false;
      this.handleMediaChange = this.handleMediaChange.bind(this);
    }

    connectedCallback() {
      this.media = window.matchMedia(SLIDER_QUERY);
      if (typeof this.media.addEventListener === 'function') {
        this.media.addEventListener('change', this.handleMediaChange);
      } else if (typeof this.media.addListener === 'function') {
        this.media.addListener(this.handleMediaChange);
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.handleMediaChange, { once: true });
      } else {
        this.handleMediaChange();
      }
    }

    disconnectedCallback() {
      if (this.media) {
        if (typeof this.media.removeEventListener === 'function') {
          this.media.removeEventListener('change', this.handleMediaChange);
        } else if (typeof this.media.removeListener === 'function') {
          this.media.removeListener(this.handleMediaChange);
        }
      }
      document.removeEventListener('DOMContentLoaded', this.handleMediaChange);
      this.destroy();
    }

    handleMediaChange() {
      if (!this.isConnected) return;
      if (this.media && this.media.matches) this.init();
      else this.destroy();
    }

    init() {
      const swiperEl = this.querySelector('[data-et-about-timeline-swiper]');
      if (!swiperEl || this.swiper || this.loading || typeof window.etLoadSwiper !== 'function') return;
      if (!this.media || !this.media.matches) return;

      this.loading = true;
      window.etLoadSwiper().then((Swiper) => {
        this.loading = false;
        if (!Swiper || !this.isConnected || this.swiper || !this.media.matches) return;

        const progressEl = this.querySelector('[data-et-about-timeline-progress]');
        const autoplay = this.autoplayEnabled();
        this.swiper = new Swiper(swiperEl, {
          slidesPerView: 'auto',
          spaceBetween: SLIDE_GAP,
          speed: 400,
          watchOverflow: true,
          grabCursor: true,
          rewind: autoplay,
          autoplay: autoplay
            ? {
                delay: parseInt(this.dataset.autoplayDelay, 10) || DEFAULT_AUTOPLAY_DELAY,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
              }
            : false,
          scrollbar: progressEl ? { el: progressEl, draggable: true, dragSize: PROGRESS_THUMB_SIZE } : false,
          keyboard: { enabled: true, onlyInViewport: true, pageUpDown: false },
          a11y: this.a11yOptions()
        });

        /* The CSS snap fallback can have scrolled the clip box by its left bleed before Swiper took over */
        swiperEl.scrollLeft = 0;

        if (this.pendingIndex !== null) {
          this.swiper.slideTo(this.pendingIndex, 0);
          this.pendingIndex = null;
        }
        if (this.blockSelected) this.pauseAutoplay();
      });
    }

    a11yOptions() {
      const options = { enabled: true };
      const label = this.dataset.slideLabel;
      if (label && label.indexOf('%index%') !== -1) {
        options.slideLabelMessage = label.replace('%index%', '{{index}}').replace('%count%', '{{slidesLength}}');
      }
      return options;
    }

    autoplayEnabled() {
      if (this.dataset.autoplay !== 'true') return false;
      return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    pauseAutoplay() {
      if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
    }

    resumeAutoplay() {
      this.blockSelected = false;
      if (this.swiper && this.swiper.autoplay && this.autoplayEnabled() && !this.swiper.autoplay.running) {
        this.swiper.autoplay.start();
      }
    }

    destroy() {
      this.pendingIndex = null;
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    selectSlide(slide) {
      const slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
      const index = slides.indexOf(slide);
      if (index < 0) return;

      this.blockSelected = true;
      this.pauseAutoplay();
      if (this.swiper) this.swiper.slideTo(index, 0);
      else if (this.media && this.media.matches) this.pendingIndex = index;
    }
  }

  window.customElements.define('et-about-timeline-slider', EtAboutTimelineSlider);

  const slidersIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-about-timeline-slider')) : [];

  document.addEventListener('shopify:section:load', (event) => slidersIn(event.target).forEach((el) => el.handleMediaChange()));
  document.addEventListener('shopify:section:unload', (event) => slidersIn(event.target).forEach((el) => el.destroy()));

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const slider = slide && slide.closest('et-about-timeline-slider');
    if (slider) slider.selectSlide(slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const slider = event.target instanceof Element ? event.target.closest('et-about-timeline-slider') : null;
    if (slider) slider.resumeAutoplay();
  });
})();
